/**
 * Semantic CDP (RudderStack) events for the booking-v4 funnel.
 *
 * These map the booking flow to a small, stable event taxonomy. Event NAMES are
 * a contract with the warehouse/destinations — change them deliberately. All
 * sends route through rudder-client, which no-ops in sandbox builds.
 *
 * NOTE: `page()` and the Google/Microsoft Ads click-id capture are already fired
 * by the GTM RudderStack snippet, so they are intentionally NOT duplicated here.
 */
import { Account } from "@hunkware/shared/types";

import { $account } from "../../../entities/account";
import { $serviceLocation } from "../../../entities/locations";
import { cdpIdentify, cdpTrack } from "./rudder-client";

/** Event-name constants — the single place to adjust the taxonomy. */
export const CDP_EVENTS = {
  LEAD_CREATED: "Lead Created",
  QUOTE_CREATED: "Quote Created",
  APPOINTMENT_BOOKED: "Appointment Booked",
  EXPERIMENT_VIEWED: "Experiment Viewed",
  PROMO_CODE_ENTERED: "Promo Code Entered",
  PROMO_CODE_VALID: "Promo Code Valid",
  PROMO_CODE_INVALID: "Promo Code Invalid",
} as const;

/** Build identify traits from the account (only fields known to exist on Account). */
const accountTraits = (account: Partial<Account>): Record<string, unknown> => ({
  firstName: account.firstName,
  lastName: account.lastName,
  email: account.email,
  phone: account.phone,
  city: account.billingAddress?.city,
  state: account.billingAddress?.state,
  categoryId: account.category?.id,
  locationId: account.location?.id,
});

/** Stable per-account user id used for identify + as the experiment unit. */
const accountUserId = (account: Partial<Account>): string =>
  `account-${account.id}`;

/**
 * Lead Created — the north-star conversion (a lead, not a purchase).
 * Fired when the account/lead is first generated (tokens/generate succeeds).
 */
export const trackLeadCreated = (): void => {
  const { account, lead } = $account.getState();
  const { location } = $serviceLocation.getState();
  if (!account?.id) return;

  cdpIdentify(accountUserId(account), accountTraits(account));
  cdpTrack(CDP_EVENTS.LEAD_CREATED, {
    accountId: account.id,
    leadId: lead?.id,
    categoryId: account.category?.id,
    locationId: location?.id ?? account.location?.id,
    email: account.email,
    phone: account.phone,
  });
};

/** Quote Created — an estimate was generated for the lead. */
export const trackQuoteCreated = (quoteId?: number | string): void => {
  const { account } = $account.getState();
  const { location } = $serviceLocation.getState();

  cdpTrack(CDP_EVENTS.QUOTE_CREATED, {
    quoteId,
    accountId: account?.id,
    categoryId: account?.category?.id,
    locationId: location?.id ?? account?.location?.id,
  });
};

/** Appointment Booked — the booking conversion. */
export const trackAppointmentBooked = (
  appointmentId?: number | string,
): void => {
  const { account, lead } = $account.getState();
  const { location } = $serviceLocation.getState();

  cdpTrack(CDP_EVENTS.APPOINTMENT_BOOKED, {
    appointmentId,
    accountId: account?.id,
    leadId: lead?.id,
    categoryId: account?.category?.id,
    locationId: location?.id ?? account?.location?.id,
  });
};

/** Shared promo-code event context (account/lead may not exist yet at the contact step). */
const promoCodeContext = (): Record<string, unknown> => {
  const { account, lead } = $account.getState();
  const { location } = $serviceLocation.getState();
  return {
    accountId: account?.id,
    leadId: lead?.id,
    categoryId: account?.category?.id,
    locationId: location?.id ?? account?.location?.id,
  };
};

/** Promo Code Entered — user clicked the "I have a promo code" reveal link. */
export const trackPromoCodeEntered = (): void => {
  cdpTrack(CDP_EVENTS.PROMO_CODE_ENTERED, promoCodeContext());
};

/** Promo Code Valid — entered code passed validation on Next. */
export const trackPromoCodeValid = (promoCode: string): void => {
  cdpTrack(CDP_EVENTS.PROMO_CODE_VALID, {
    promoCode,
    ...promoCodeContext(),
  });
};

/** Promo Code Invalid — entered code failed validation on Next. */
export const trackPromoCodeInvalid = (
  promoCode: string,
  reason?: string,
): void => {
  cdpTrack(CDP_EVENTS.PROMO_CODE_INVALID, {
    promoCode,
    reason,
    ...promoCodeContext(),
  });
};

/**
 * Experiment Viewed — GrowthBook exposure, mirrored into the CDP so experiment
 * results can be computed in the warehouse alongside the funnel conversions.
 */
export const trackExperimentViewed = (
  experimentId: string,
  variationId: string,
): void => {
  cdpTrack(CDP_EVENTS.EXPERIMENT_VIEWED, {
    experiment_id: experimentId,
    variation_id: variationId,
  });
};
